// Assigned-worker section of the Shift Detail panel: a source/status label, the worker identity
// row (or an open-slot placeholder), the fit score when the worker is a known candidate, and the
// context actions — Accept / Reject for a pending Works suggestion, Unassign for a filled shift.
import React from 'react';
import {
  Button,
  Gap,
  Gutter,
  List,
  Modal,
  Text,
  UserAvatar,
} from '@common/components';
import { Colors } from '@common/tokens';
import type { ScheduleShiftDecisionType } from '@load-balancing/types';
import type { ShiftContextType } from './_schedule-shift-detail-panel-service';
import { ScheduleShiftDetailWorkerIdentity } from './_schedule-shift-detail-worker-identity';

const OUTSIDE_SERVICE_LINE_TOOLTIP =
  "This worker isn't part of this service line, so their profile isn't available here.";

export function ScheduleShiftDetailAssigned({
  context,
  unitName,
  role,
  // fitScore, // @TODO: reenable with the fit-score chip in the second release
  reasons,
  readOnly = false,
  unassigning = false,
  onUnassign,
  // onDecision,
  onWorkerSelect,
}: {
  context: ShiftContextType;
  /** Shift's unit, shown as the worker's home unit when the roster can't enrich the name. */
  unitName?: string;
  /** Shift's role, shown alongside the unit. */
  role: string;
  fitScore: number | null;
  reasons: string[];
  readOnly?: boolean;
  /** An unassignShift is in flight — show the Unassign button busy and block re-clicks. */
  unassigning?: boolean;
  onUnassign?: () => void;
  onDecision?: (decision: ScheduleShiftDecisionType) => void;
  onWorkerSelect?: (memberId: string) => void;
}) {
  const { kind, member, workerName, label, showUnassign } = context;
  // A named worker with no roster member is staffed here from outside the service line — expected,
  // and we have no profile to open for them (WORK-6573). Their name stays non-linked, with a
  // tooltip saying why, instead of silently rendering as plain text.
  const outsideServiceLine = !member && Boolean(workerName);
  const assignedTint =
    kind === 'existing' ||
    kind === 'manual_edit' ||
    kind === 'suggestion_accepted';

  // A worker that doesn't resolve to a team-roster member is from outside this service line:
  // they don't appear in the Team tab and can't be reassigned here, so unassigning them is
  // effectively irreversible from this view (WORK-6514). Confirm before removing; an in-roster
  // worker unassigns directly.
  const handleUnassign = () => {
    if (!onUnassign) return;

    if (member) {
      onUnassign();
      return;
    }

    Modal.confirm({
      title: 'Unassign staff from this shift?',
      content: `${workerName ?? 'This worker'} isn't on your service line's roster. If you unassign them, this shift can't be reassigned to them. This action can't be undone.`,
      okText: 'Unassign',
      danger: true,
      onOk: onUnassign,
    });
  };

  const actionSlot = (
    <Gutter direction="top" size="xxs">
      <Gap size="sm" align="center">
        {/* @TODO: reenable it to the second release */}
        {/* {fitScore !== null && (
                <Text size="md" weight="medium" color={scoreColor(fitScore)}>
                  {fitScore}
                </Text>
              )} */}
        {!readOnly && showUnassign && onUnassign && (
          <Button
            kind="link"
            onClick={handleUnassign}
            size="sm"
            loading={unassigning}
          >
            Unassign
          </Button>
        )}
      </Gap>
    </Gutter>
  );

  return (
    <Gap vertical size="sm" align="start" fullWidth>
      <Text size="md" secondary>
        {label}
      </Text>

      {kind === 'open_slot' ? (
        <Gap size="sm" align="center">
          <UserAvatar
            size={34}
            textAsInitials="—"
            backgroundColor={Colors.steelLightest}
            textColor={Colors.midnightLighter}
            borderColor={Colors.steelLighter}
          />
          <Text size="md" color="midnightLighter">
            No one assigned
          </Text>
        </Gap>
      ) : (
        <Gap size="sm" align="start" justify="space-between" fullWidth>
          <ScheduleShiftDetailWorkerIdentity
            name={workerName ?? ''}
            unit={member?.unit ?? unitName}
            role={member?.role ?? role}
            member={member}
            bold
            avatarVariant={assignedTint ? 'assigned' : 'default'}
            onClick={
              member && onWorkerSelect
                ? () => onWorkerSelect(member.id)
                : undefined
            }
            unavailableTooltip={
              outsideServiceLine ? OUTSIDE_SERVICE_LINE_TOOLTIP : undefined
            }
            actionSlot={actionSlot}
          />
        </Gap>
      )}

      {!readOnly && reasons.length > 0 && (
        <Gutter direction="top" size="sm">
          <Gap vertical size="sm" align="start" fullWidth>
            {reasons.length > 0 && (
              <Gap vertical size="xs" align="start">
                <Text color="midnightRegular">Why this move?</Text>
                <List>
                  {reasons.map((reason) => (
                    <List.Item key={reason}>
                      <Text color="midnightRegular">{reason}</Text>
                    </List.Item>
                  ))}
                </List>
              </Gap>
            )}
            {/* @TODO: reenable it to the second release */}
            {/* <Gap size="md" align="center">
              <Button
                kind="primary"
                size="sm"
                onClick={() => onDecision?.('accepted')}
              >
                Accept
              </Button>
              <Button
                kind="link"
                size="sm"
                danger
                onClick={() => onDecision?.('rejected')}
              >
                Reject
              </Button>
            </Gap> */}
          </Gap>
        </Gutter>
      )}
    </Gap>
  );
}
